import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, MessageCircle, Wrench } from "lucide-react";
import { api, rp } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

const STATES = [
  ["REFUND_PENDING", "Siap direfund"],
  ["QUICK_CHECK_REQUIRED", "Menunggu cek cepat"],
  ["DEEP_INSPECTION_REQUIRED", "Perlu inspeksi mendalam"],
  ["DEPOSIT_HELD", "Deposit ditahan"],
  ["REFUNDED", "Sudah direfund"],
  ["all", "Semua"],
];

const SETTLED = ["REFUNDED", "PARTIALLY_REFUNDED", "FORFEITED"];

const EMAIL_NOTE = {
  queued: "Email kuitansi otomatis sudah dikirim ke customer.",
  no_email: "Customer belum mengisi email — kirim lewat WhatsApp saja.",
  test_data: "Data uji — email sungguhan tidak dikirim.",
  duplicate: "Email untuk kejadian ini sudah pernah dikirim.",
  error: "Email gagal disiapkan — kirim lewat WhatsApp.",
};

export default function Returns() {
  const { user } = useAuth();
  const [state, setState] = useState("REFUND_PENDING");
  const [rows, setRows] = useState([]);
  const [due, setDue] = useState([]);
  const [pending, setPending] = useState([]);
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState("");
  const [deep, setDeep] = useState({ asset_code: "", outcome: "ok", findings: "", repair_cost: 0 });
  const canDecide = ["owner", "finance"].includes(user?.role)
    || (user?.perms || []).includes("payments:write");
  const canViewDeposits = canDecide;
  const canInspect = user?.role === "owner";

  const load = useCallback(() => {
    if (canViewDeposits) {
      api.get("/returns/deposit-queue", { params: { state } })
        .then((r) => setRows(r.data))
        .catch((e) => toast.error(e?.response?.data?.detail || "Gagal memuat"));
    } else {
      setRows([]);
    }
    if (canInspect) {
      api.get("/returns/deep-inspection-due").then((r) => setDue(r.data)).catch(() => setDue([]));
      api.get("/returns/pending").then((r) => setPending(r.data)).catch(() => setPending([]));
    } else {
      setDue([]);
      setPending([]);
    }
  }, [canInspect, canViewDeposits, state]);
  useEffect(() => { load(); }, [load]);

  const decide = async (row) => {
    const f = form[row.id] || {};
    setBusy(row.id);
    try {
      const r = await api.post(`/returns/${row.id}/deposit-decision`, {
        deduction_amount: Number(f.deduction || 0), reason: f.reason || "",
        method: "Bank Transfer",
      });
      toast.success(`Deposit: ${r.data.deposit_state}`);
      const notice = r.data.customer_notice;
      if (notice) toast.message(EMAIL_NOTE[notice.email_status] || "Notifikasi customer disiapkan");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal memproses deposit");
    } finally { setBusy(""); }
  };

  const sendWa = async (row) => {
    try {
      const r = await api.get(`/customer-messages/${row.id}/wa`,
        { params: { event: "deposit_settled" } });
      if (!r.data.wa_url) return toast.error("Nomor WhatsApp customer tidak valid");
      window.open(r.data.wa_url, "_blank");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal membuat template");
    }
  };

  const closeDeep = async () => {
    setBusy("deep");
    try {
      await api.post("/returns/deep-inspection", { ...deep, repair_cost: Number(deep.repair_cost || 0) });
      toast.success("Inspeksi mendalam dicatat");
      setDeep({ asset_code: "", outcome: "ok", findings: "", repair_cost: 0 });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal menyimpan inspeksi");
    } finally { setBusy(""); }
  };

  return (
    <div data-testid="admin-returns" className="pb-24">
      <h1 className="font-display text-3xl tracking-tight">Pengembalian &amp; Deposit</h1>
      <p className="mt-1 text-sm text-ink-soft max-w-2xl">
        {canInspect && "Cek cepat yang gagal masuk antrean inspeksi mendalam. "}
        Deposit bukan pendapatan — refund dan pemotongan tidak masuk komisi.
      </p>

      {canViewDeposits && (
        <div className="mt-5 flex flex-wrap gap-2">
          {STATES.map(([k, label]) => (
            <button key={k} data-testid={`returns-tab-${k}`} onClick={() => setState(k)}
              className={`text-xs px-3 py-1.5 rounded-full border ${state === k ? "bg-lm-red text-white border-lm-red" : "border-black/15 hover:border-lm-red/50"}`}>
              {label}
            </button>
          ))}
        </div>
      )}

      {canViewDeposits && rows.length === 0 && (
        <div data-testid="returns-empty" className="mt-6 rounded-lg border border-dashed border-black/15 p-10 text-center text-sm text-ink-soft">
          Tidak ada booking pada status ini.
        </div>
      )}

      {canViewDeposits && <div className="mt-5 grid gap-4 lg:grid-cols-2">
        {rows.map((r) => (
          <div key={r.id} data-testid={`deposit-card-${r.id}`} className="bg-white border border-black/10 rounded-lg p-5">
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="font-semibold">{r.booking_code}</div>
                <div className="text-xs text-ink-soft mt-0.5">{r.customer_name} · {r.branch_name || "-"} · {r.rental_date}</div>
              </div>
              <span className="text-[11px] px-2 py-1 rounded-full bg-neutral-100 border border-neutral-200" data-testid={`deposit-state-${r.id}`}>
                {r.deposit_state || "—"}
              </span>
            </div>
            <div className="mt-3 space-y-1 text-sm">
              <Row l="Deposit ditahan" v={rp(r.deposit_amount || 0)} />
              <Row l="Per meja" v={`${rp(r.deposit_per_unit || 0)} × ${r.quantity}`} />
              <Row l="Meja kembali" v={`${r.units_returned_count || 0} / ${r.quantity}`} />
              <Row l="Rekening refund" v={`${r.refund_bank_name || "-"} · ${r.refund_account_number || r.refund_account_number_masked || "-"}`} />
            </div>
            {canDecide && SETTLED.includes(r.deposit_state) && (
              <div className="mt-4 border-t border-black/10 pt-4">
                <button data-testid={`wa-deposit-${r.id}`} onClick={() => sendWa(r)}
                  className="text-xs inline-flex items-center gap-1.5 px-4 py-2.5 rounded-md border border-emerald-300 text-emerald-800 hover:bg-emerald-50">
                  <MessageCircle size={13} /> Template WA: deposit sudah direfund
                </button>
                <p className="mt-2 text-[11px] text-ink-soft">
                  Pesan sudah terisi; lampirkan foto bukti refund sebelum mengirim ke customer.
                </p>
              </div>
            )}
            {canDecide && !SETTLED.includes(r.deposit_state) && (
              <div className="mt-4 border-t border-black/10 pt-4 space-y-3">
                <Input data-testid={`deduction-${r.id}`} type="number" placeholder="Pemotongan (Rp) — 0 = refund penuh"
                  value={(form[r.id] || {}).deduction ?? ""}
                  onChange={(e) => setForm((p) => ({ ...p, [r.id]: { ...(p[r.id] || {}), deduction: e.target.value } }))} />
                <Textarea data-testid={`deduction-reason-${r.id}`} rows={2} placeholder="Alasan pemotongan (wajib kalau ada potongan)"
                  value={(form[r.id] || {}).reason ?? ""}
                  onChange={(e) => setForm((p) => ({ ...p, [r.id]: { ...(p[r.id] || {}), reason: e.target.value } }))} />
                <button data-testid={`decide-${r.id}`} disabled={busy === r.id} onClick={() => decide(r)}
                  className="lm-btn-primary rounded-md px-4 py-2.5 text-xs font-semibold inline-flex items-center gap-2 disabled:opacity-40">
                  {busy === r.id && <Loader2 size={13} className="animate-spin" />} Proses deposit
                </button>
              </div>
            )}
          </div>
        ))}
      </div>}

      {canInspect && <div className="mt-10 grid gap-6 lg:grid-cols-2">
        <div className="bg-white border border-black/10 rounded-lg p-5">
          <div className="text-sm font-semibold">Aset menunggu cek cepat</div>
          <div className="mt-3 space-y-2 text-sm" data-testid="pending-quick-checks">
            {pending.length === 0 && <div className="text-ink-soft text-xs">Semua aset sudah dicek.</div>}
            {pending.map((p) => (
              <div key={p.id} className="flex justify-between gap-3 border-b border-black/5 pb-2">
                <span>{p.asset_code} · {p.booking_code}</span>
                <span className="text-xs text-ink-soft">{p.quick_check_status}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white border border-black/10 rounded-lg p-5">
          <div className="text-sm font-semibold inline-flex items-center gap-2"><Wrench size={15} /> Inspeksi mendalam</div>
          <div className="mt-3 space-y-1 text-xs" data-testid="deep-due-list">
            {due.length === 0 && <div className="text-ink-soft">Tidak ada aset yang jatuh tempo.</div>}
            {due.slice(0, 12).map((d) => (
              <div key={d.unit_id} className="flex justify-between gap-3">
                <span>{d.asset_code}</span><span className="text-ink-soft">{d.reason}</span>
              </div>
            ))}
          </div>
          <div className="mt-4 space-y-3">
            <Input data-testid="deep-asset" placeholder="Asset ID — LM-JKT-001" value={deep.asset_code}
              onChange={(e) => setDeep({ ...deep, asset_code: e.target.value.toUpperCase() })} />
            <select data-testid="deep-outcome" value={deep.outcome} onChange={(e) => setDeep({ ...deep, outcome: e.target.value })}
              className="w-full h-11 rounded-md border border-input bg-white px-3 text-sm">
              <option value="ok">Aman — kembali siap sewa</option>
              <option value="repair_needed">Perlu perbaikan</option>
              <option value="retired">Pensiunkan aset</option>
            </select>
            <Textarea data-testid="deep-findings" rows={2} placeholder="Temuan" value={deep.findings}
              onChange={(e) => setDeep({ ...deep, findings: e.target.value })} />
            <Input data-testid="deep-cost" type="number" placeholder="Biaya perbaikan (Rp)" value={deep.repair_cost}
              onChange={(e) => setDeep({ ...deep, repair_cost: e.target.value })} />
            <button data-testid="deep-submit" disabled={busy === "deep" || !deep.asset_code} onClick={closeDeep}
              className="lm-btn-primary rounded-md px-4 py-2.5 text-xs font-semibold disabled:opacity-40">
              Tutup inspeksi
            </button>
          </div>
        </div>
      </div>}
    </div>
  );
}

const Row = ({ l, v }) => (
  <div className="flex justify-between gap-4"><span className="text-ink-soft">{l}</span><span className="text-right">{v}</span></div>
);
